import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { VENDOR_LOGOS } from '^/constants/channel/logoImages.constants';
import { birthdayState } from '^/recoil/atoms';
import { pickupLocationState, returnLocationState } from '^/recoil/atoms/locationState';
import { pickupDatetimeState, returnDatetimeState } from '^/recoil/atoms/datetimeState';
import { authAtom } from '../../recoil/atoms/auth';
import { typeRouter } from '^/typeRoute/typeRoutes';
import { translateServices } from '../Listing/common/translateServices';
import { parseRentDateTime } from '^/util/rentDateTime';
import { getCarName } from '^/util/getCarName';
import { RentalInfoSummary } from '../Booking/renewal/components/RentalInfoSummary/RentalInfoSummary';
import { formatEquipmentString } from '^/util/formatEquipmentString';

function gotoMyReservationByRefresh() {
  const myReservationPath = typeRouter.routes.myReservationsList().href;
  window.location.href = myReservationPath;
}

function goToHomeByRefresh() {
  window.location = window.location.origin;
}

const ReservationResultHertz = ({ reservationData }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const isAuth = useRecoilValue(authAtom);
  const setPickupDatetime = useSetRecoilState(pickupDatetimeState);
  const setReturnDatetime = useSetRecoilState(returnDatetimeState);
  const setBirthday = useSetRecoilState(birthdayState);
  const setPickupLocation = useSetRecoilState(pickupLocationState);
  const setReturnLocation = useSetRecoilState(returnLocationState);

  const [resInfo, setResInfo] = useState({});

  const { bookingInfo } = reservationData;

  const { parsedPickupTime, parsedReturnTime, timeDiff } = parseRentDateTime(
    bookingInfo?.carInfo?.coreInfo?.pickup?.time,
    bookingInfo?.carInfo?.coreInfo?.return?.time,
    'YYYY.MM.DD (ddd) HH:mm',
  );

  const setupData = () => setResInfo(reservationData.reservationInfo);

  useEffect(() => {
    setupData();
  }, []);

  useEffect(() => {
    setPickupDatetime('');
    setReturnDatetime('');
    setBirthday('');
    setPickupLocation({
      cityCode: '',
      cityName: '',
      parentCategoryCode: '',
      categories: {},
      locationCode: '',
      locationName: '',
    });
    setReturnLocation({
      cityCode: '',
      cityName: '',
      parentCategoryCode: '',
      categories: {},
      locationCode: '',
      locationName: '',
    });
  }, [resInfo]);

  const {
    vehInfo,
    services,
    coreInfo: officeInfo,
    equipsCharge,
    pkgSelected,
    insurances,
  } = bookingInfo.carInfo;
  const packageInfo = insurances.find((i) => i.package === pkgSelected).packageInfo;

  if (isMobile) {
    return (
      <>
        <h1 className="p-[20px] text-semihuge font-bold">예약이 완료되었습니다!</h1>
        <div className="w-full max-w-[715px] flex flex-col justify-between pb-[30px] relative tablet-max:max-w-none tablet-max:items-center tablet-max:mx-auto tablet-max:px-[20px] tablet-max:py-[15px]">
          <div className="w-full flex flex-col items-center justify-center gap-[10px]">
            <img width={116} src={VENDOR_LOGOS[vehInfo?.company]} alt={vehInfo?.company} />
            <h1 className="text-[#1e1e1e] text-[16px] font-bold leading-[16px] ml-[5px] break-keep text-center">
              [{t(vehInfo?.size)}] {getCarName(vehInfo?.name)}
            </h1>
          </div>
          <div className="text-[14px] text-[#1e1e1e] font-light tablet-max:text-[12px] tablet-max:leading-[12px] tablet-max:mt-[5px]">
            {services
              ?.filter((service) => service.info && service.info !== 'undefined')
              // eslint-disable-next-line no-template-curly-in-string
              ?.map(
                (service) =>
                  `${
                    Number(service.info)
                      ? `${t(service.shortName).includes('${num}') ? '' : service.info}`
                      : // eslint-disable-next-line no-template-curly-in-string
                        t(service.info)
                  } ${t(service.shortName).replace('${num}', service.info)}`,
              )
              .join(' / ')}
          </div>
          <img
            className="mt-[30px] w-[158px] tablet-max:mt-[10px]"
            src={vehInfo?.image}
            alt={vehInfo?.name}
          />
          <div className="text-[#9f9f9f] text-[12px] font-light leading-[22px]">
            실제 대여하실 차량과 다를 수 있습니다.
          </div>
          <RentalInfoSummary
            officeInfo={{
              pickup: {
                code: officeInfo.pickup.code,
                time: officeInfo.pickup.time,
              },
              return: {
                code: officeInfo.return.code,
                time: officeInfo.return.time,
              },
            }}
            equipsCharge={equipsCharge}
            packageInfo={packageInfo}
            pkgSelected={pkgSelected}
            hideInsuranceInfoIcon
          />
        </div>

        <div className="flex gap-[15px] justify-between items-center w-full mt-5 px-[20px]">
          <button
            onClick={() => goToHomeByRefresh()}
            className="border border-neutral-100 basis-1/2 tablet:py-4 tablet:px-6 mobile:p-[15px] bg-white rounded-trimo-5 text-primary-gray font-bold"
          >
            {t('backHome')}
          </button>
          <button
            onClick={gotoMyReservationByRefresh}
            disabled={isAuth ? false : true}
            className="basis-1/2 tablet:py-4 tablet:px-6 mobile:p-[15px] bg-primary-skyblue tablet:bg-trimo-shadow rounded-trimo-5 text-primary-white font-bold whitespace-nowrap"
          >
            {t('toReservationHistory')}
          </button>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="w-full max-w-[1060px] mx-auto relative flex flex-col mb-[20px]">
        <div className="mx-auto flex flex-col gap-6 w-full max-w-[1060px] mt-[50px]">
          <h1 className="text-giant font-bold tablet-max:text-semihuge">예약이 완료되었습니다!</h1>
          <div className="bg-primary-white rounded-[10px] shadow-trimodropdown p-[30px] text-primary-gray w-full">
            <div className="flex justify-between">
              <div className="flex flex-col gap-[10px]">
                <img
                  alt="car-image"
                  src={VENDOR_LOGOS[bookingInfo?.carInfo?.vehInfo?.company]}
                  className="object-scale-down w-[116px] mb-[10px]"
                />
                <p className="text-semihuge font-bold">
                  [{t(vehInfo?.size)}] {getCarName(vehInfo?.name)}
                </p>

                <p className="text-default text-[#1E1E1E]">
                  {translateServices(bookingInfo.carInfo.services, t)}
                </p>
              </div>
              <div className="flex flex-col items-center justify-center mt-[20px]">
                <img src={bookingInfo.carInfo.vehInfo.image} width={158} />
                <p className="text-xs mt-[10px] text-[#9f9f9f]">
                  실제 대여하실 차량과 다를 수 있습니다.
                </p>
              </div>
            </div>
            <div className="mt-[50px] flex flex-col gap-[15px] text-left">
              <div className="flex justify-between items-center gap-[20px]">
                <div className="w-[140px] text-semihuge font-bold">대여/반납 장소</div>
                <div className="grow">
                  {t(`location_name.${bookingInfo?.carInfo?.coreInfo?.pickup?.code}`) +
                    ' / ' +
                    t(`location_name.${bookingInfo?.carInfo?.coreInfo?.return?.code}`)}
                </div>
              </div>
              <div className="flex justify-between items-center gap-[20px]">
                <div className="w-[140px] tablet:text-semihuge font-bold">대여/반납 일시</div>
                <div className="grow tablet:text-big mobile:text-default">
                  <span>
                    {parsedPickupTime} ~ {parsedReturnTime}
                  </span>
                  <span> ({timeDiff})</span>
                </div>
              </div>
              <div className="flex justify-between items-center gap-[20px]">
                <div className="w-[140px] text-semihuge font-bold">보험 내용</div>
                <div className="grow tablet:text-big mobile:text-default flex flex-row items-center">
                  <p>{pkgSelected ? t(`insurance_name.${pkgSelected}`) : '기본 할인요금'}</p>
                </div>
              </div>
              <div className="flex justify-between items-center gap-[20px]">
                <div className="w-[140px] text-semihuge font-bold">추가 옵션</div>
                <div className="grow text-big">
                  {bookingInfo?.carInfo.equipsCharge?.length > 0 ? (
                    <span>{formatEquipmentString(bookingInfo?.carInfo?.equipsCharge, t)}</span>
                  ) : (
                    <span>없음</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex tablet:gap-6 mobile:gap-[15px] justify-between items-center w-full">
            <button
              onClick={() => goToHomeByRefresh()}
              className="basis-1/2 tablet:py-[13px] tablet:px-6 bg-trimo-shadow rounded-trimo-5 text-default"
            >
              홈으로
            </button>
            <button
              onClick={gotoMyReservationByRefresh}
              disabled={isAuth ? false : true}
              className="basis-1/2 tablet:py-[13px] tablet:px-6 bg-primary-skyblue rounded-trimo-5 text-primary-white whitespace-nowrap text-default"
            >
              예약 내역 확인하기
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReservationResultHertz;
